import "./style.scss";

import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { IconButton, Tooltip } from "@mui/material";
import {
    formatManagementAdvanceAmount,
    formatManagementAmount,
    resolvePreviewAdvanceAmountCents,
    type TpdLike,
} from "components/AdvanceManagement/utils/advanceSliceAmounts";
import { applyAdvancePaidInPeriodImpact } from "components/AdvanceManagement/utils/advanceTpdImpact";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import ManagementTableTitleComponent from "components/shared/ManagementTableTitleComponent";
import NavigationPreview from "components/shared/NavigationPreview";
import PeriodSelectorPreview from "components/shared/PeriodSelectorPreview";
import { Body } from "helpers/TypographyComponents";
import { useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { TPeriod } from "store/periodStore";

const SECTION_TITLE_WITH_NAV_STYLE: CSSProperties = {
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.35rem",
};

export interface ITermManagementPreviewDisplay {
    title: string;
    isbn?: string;
    titleAvatarUrl?: string;
    author: string;
    representative?: string | null;
    beneficiaryAvatarUrl?: string;
    isTermDeleted?: number;
}

interface ManagementPreviewRow {
    id: number;
    display: ITermManagementPreviewDisplay;
    balancePrev: number;
    advanceAmount: number;
    grossDuty: number;
    balance: number;
    currentAdvanceAmount: number;
    currentGrossDuty: number;
    currentBalance: number;
    hasPendingChanges: boolean;
}

interface ITermManagementPreviewSection {
    display: ITermManagementPreviewDisplay;
    tpd?: TpdLike;
    period?: TPeriod;
    /** Existing advance total in management (positive cents). */
    currentAdvanceAmountCents?: number;
    previewAdvanceAmountCents?: number;
    isLoading?: boolean;
    showViewInManagement?: boolean;
    onViewInManagement?: () => void;
}

function AmountChangeCell({
    currentCents,
    previewCents,
    formatAmount = formatManagementAmount,
}: {
    currentCents: number;
    previewCents: number;
    formatAmount?: (cents: number) => string;
}) {
    if (currentCents === previewCents) {
        return <>{formatAmount(previewCents)}</>;
    }

    return (
        <span className="term-management-preview-section__amount-delta">
            <span className="term-management-preview-section__amount-delta-old">{formatAmount(currentCents)}</span>
            <span className="term-management-preview-section__amount-delta-new">{formatAmount(previewCents)}</span>
        </span>
    );
}

const TermManagementPreviewSection = ({
    display,
    tpd,
    period,
    currentAdvanceAmountCents = 0,
    previewAdvanceAmountCents,
    isLoading = false,
    showViewInManagement = false,
    onViewInManagement,
}: ITermManagementPreviewSection) => {
    const [t] = useTranslation();

    const isEditPreview = previewAdvanceAmountCents != null;

    const tableData = useMemo((): ManagementPreviewRow[] => {
        const previewAdvanceAmount = resolvePreviewAdvanceAmountCents(tpd, previewAdvanceAmountCents);
        const currentAdvanceAmount = currentAdvanceAmountCents;
        const currentGrossDuty = tpd?.grossDuty ?? 0;
        const currentBalance = tpd?.nextBalance ?? 0;
        const advanceAmountChanged = currentAdvanceAmount !== previewAdvanceAmount;
        const previewImpact = advanceAmountChanged
            ? applyAdvancePaidInPeriodImpact(tpd, previewAdvanceAmount)
            : { grossDuty: currentGrossDuty, nextBalance: currentBalance };
        const hasPendingChanges =
            isEditPreview &&
            (advanceAmountChanged ||
                currentGrossDuty !== previewImpact.grossDuty ||
                currentBalance !== previewImpact.nextBalance);

        return [
            {
                id: tpd?.id ?? 0,
                display,
                balancePrev: tpd?.balance ?? 0,
                advanceAmount: previewAdvanceAmount,
                grossDuty: previewImpact.grossDuty,
                balance: previewImpact.nextBalance,
                currentAdvanceAmount: currentAdvanceAmount,
                currentGrossDuty,
                currentBalance,
                hasPendingChanges,
            },
        ];
    }, [display, tpd, previewAdvanceAmountCents, currentAdvanceAmountCents, isEditPreview]);

    const columns = useMemo((): LastElementTableColumns[] => {
        const amountFormat = (value: number) => formatManagementAmount(Number(value));

        const baseColumns: LastElementTableColumns[] = [
            {
                id: "titleCell",
                label: t("Works.WorkLabel"),
                align: "left",
                avatar: false,
                minWidth: "140px",
                maxWidth: "180px",
                stopPropagation: true,
                format: (_value, _id, row: ManagementPreviewRow) => (
                    <ManagementTableTitleComponent
                        title={row.display.title}
                        isbn={row.display.isbn}
                        titleCoverUrl={row.display.titleAvatarUrl}
                        isTermDeleted={row.display.isTermDeleted}
                        sx={{ marginLeft: 0, width: "auto" }}
                    />
                ),
            },
            {
                id: "authorCell",
                label: t("Management.Author"),
                align: "left",
                avatar: false,
                minWidth: "120px",
                maxWidth: "160px",
                stopPropagation: true,
                format: (_value, _id, row: ManagementPreviewRow) => (
                    <ManagementTableAuthorComponent
                        author={row.display.author}
                        representative={row.display.representative ?? undefined}
                        avatarUrl={row.display.beneficiaryAvatarUrl}
                        showAvatar
                        maxWidth="100%"
                    />
                ),
            },
            {
                id: "balancePrev",
                label: t("Management.BalancePrev"),
                align: "right",
                minWidth: "72px",
                format: amountFormat,
            },
            {
                id: "advanceAmount",
                label: t("Management.AdvanceAmount"),
                align: "right",
                minWidth: "72px",
                format: (_value, _id, row: ManagementPreviewRow) =>
                    isEditPreview ? (
                        <AmountChangeCell
                            currentCents={row.currentAdvanceAmount}
                            previewCents={row.advanceAmount}
                            formatAmount={formatManagementAdvanceAmount}
                        />
                    ) : (
                        formatManagementAdvanceAmount(row.advanceAmount)
                    ),
            },
            {
                id: "grossDuty",
                label: t("Management.GrossDuty"),
                align: "right",
                minWidth: "72px",
                format: (_value, _id, row: ManagementPreviewRow) =>
                    isEditPreview ? (
                        <AmountChangeCell currentCents={row.currentGrossDuty} previewCents={row.grossDuty} />
                    ) : (
                        amountFormat(row.grossDuty)
                    ),
            },
            {
                id: "balance",
                label: t("Management.Balance"),
                align: "right",
                minWidth: "72px",
                format: (_value, _id, row: ManagementPreviewRow) =>
                    isEditPreview ? (
                        <AmountChangeCell currentCents={row.currentBalance} previewCents={row.balance} />
                    ) : (
                        amountFormat(row.balance)
                    ),
            },
        ];

        if (!showViewInManagement) {
            return baseColumns;
        }

        return [
            ...baseColumns,
            {
                id: "viewInManagement",
                label: "",
                align: "center",
                minWidth: "48px",
                stopPropagation: true,
                format: () => (
                    <Tooltip title={t("AdvancePayment.ViewInManagement")} arrow>
                        <IconButton
                            size="small"
                            className="management-preview-table__action-btn"
                            aria-label={t("AdvancePayment.ViewInManagement")}
                            onClick={onViewInManagement}
                            disabled={!onViewInManagement}
                        >
                            <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ];
    }, [t, showViewInManagement, onViewInManagement, isEditPreview]);

    const hasPendingChanges = tableData[0]?.hasPendingChanges ?? false;

    return (
        <div className="term-management-preview-section">
            <Body style={SECTION_TITLE_WITH_NAV_STYLE} tag="div">
                {t("AdvancePayment.ManagementPreviewManagementSectionPrefix")}
                <NavigationPreview />
                {period && (
                    <>
                        <span className="term-management-preview-section__pill-separator" aria-hidden>
                            {"<>"}
                        </span>
                        <PeriodSelectorPreview period={period} />
                    </>
                )}
            </Body>
            <LastElementTable
                tableClassName="management-preview-table"
                columns={columns}
                data={tableData}
                max={1}
                columnsNumber={columns.length}
                isLoading={isLoading}
                loadingRowsCount={1}
                bottomFooterContent={<></>}
                tableBodyStyles={{
                    fontWeight: 400,
                    fontSize: "11px",
                    fontFamily: "'Poppins', sans-serif",
                    borderColor: "#eaeaea",
                    whiteSpace: "nowrap",
                    padding: "4px 8px",
                }}
            />
            {hasPendingChanges && (
                <p className="term-management-preview-section__pending-hint">
                    {t("AdvancePayment.ManagementPreviewPendingChanges")}
                </p>
            )}
        </div>
    );
};

export default TermManagementPreviewSection;
